import Image from "next/image"
import { cn } from "@/lib/utils"
import { HttpTypes, StoreProduct } from "@medusajs/types"
import LocalizedClientLink from "@/components/molecules/LocalizedLink/LocalizedLink"
import { useTranslate } from "@tolgee/react"
import { BaseHit, Hit } from "instantsearch.js"
import { getProductPrice } from "@/lib/helpers/get-product-price"


export const CompactProductCard = ({
  product,
  apiProduct,
  variantId,
  locale,
  className,
  priority = false,
}: {
  product: Hit<HttpTypes.StoreProduct> | Partial<Hit<BaseHit>>
  apiProduct?: HttpTypes.StoreProduct | null
  variantId?: string
  locale?: string,
  className: string,
  priority?: boolean,
}) => {

  const { t } = useTranslate();
  if (!apiProduct) {
    return <></>
  }

  const { cheapestVariant, cheapestPrice, variant, variantPrice } = getProductPrice({
    product: apiProduct! as HttpTypes.StoreProduct,
    variantId: variantId,
  })

  const displayVariant = variantId ? variant : cheapestVariant
  const displayPrice = variantId ? variantPrice : cheapestPrice
  const isAvailable = displayVariant && displayPrice?.calculated_price

  // console.log('displayVariant: ~~~~~~~~~~~~~~ ', variantId, displayVariant, displayPrice, cheapestVariant, cheapestPrice, variant, variantPrice)

  return (
    <LocalizedClientLink
      href={`/products/${product.handle}`}
      className={cn(
        "group flex w-full flex-col overflow-hidden rounded-lg border border-border bg-background shadow-sm transition-all duration-300 hover:scale-[1.02] hover:shadow-lg",
        className,
      )}
      aria-label={`View product: ${product.title}`}
    >
      <div className="relative aspect-square overflow-hidden bg-secondary/10">
        {product.thumbnail ? (
          <Image
            src={decodeURIComponent(product.thumbnail)}
            alt={t("product_card.product_placeholder")}
            unoptimized
            className="object-cover transition-transform duration-500 ease-out group-hover:scale-110"
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 240px"
            fill
          />
        ) : (
          <Image
            src="/images/placeholder.svg"
            alt={t("product_card.product_placeholder")}
            className="object-cover transition-transform duration-500 ease-out group-hover:scale-110"
            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 240px"
            fill
          />
        )}

      </div>

      <div className="flex flex-1 flex-col p-4">
        <h3 className="mb-2 line-clamp-2 text-sm font-semibold text-foreground">{product.title}</h3>

        {isAvailable && displayPrice && (
          <div className="mt-auto flex items-baseline justify-between">
            <span className="text-xs text-muted-foreground">From</span>
            <span className="text-base font-bold text-primary">
              <p className="font-medium">{displayPrice?.calculated_price}</p>
              {displayPrice?.calculated_price !==
                displayPrice?.original_price && (
                  <p className="text-sm text-gray-500 line-through">
                    {displayPrice?.original_price}
                  </p>
                )}
            </span>
          </div>
        )}
      </div>
    </LocalizedClientLink>
  )
}
